import React from 'react';
import styled from 'styled-components';
import {
  CONTAINER_STYLES,
  getTextDifferences,
  HIGHLIGHT_STYLES,
} from './utils/highlighting.utils';

const HighlightedSpan = styled.span<{
  highlightType: 'added' | 'removed' | 'unchanged';
}>`
  ${(props) => {
    switch (props.highlightType) {
      case 'added':
        return `
          background-color: ${HIGHLIGHT_STYLES.added.backgroundColor};
          color: ${HIGHLIGHT_STYLES.added.color};
          padding: ${HIGHLIGHT_STYLES.added.padding};
          border-radius: ${HIGHLIGHT_STYLES.added.borderRadius};
          box-decoration-break: ${HIGHLIGHT_STYLES.added.boxDecorationBreak};
          -webkit-box-decoration-break: ${HIGHLIGHT_STYLES.added.WebkitBoxDecorationBreak};
        `;
      case 'removed':
        return `
          background-color: ${HIGHLIGHT_STYLES.removed.backgroundColor};
          color: ${HIGHLIGHT_STYLES.removed.color};
          text-decoration: ${HIGHLIGHT_STYLES.removed.textDecoration};
          padding: ${HIGHLIGHT_STYLES.removed.padding};
          border-radius: ${HIGHLIGHT_STYLES.removed.borderRadius};
          box-decoration-break: ${HIGHLIGHT_STYLES.removed.boxDecorationBreak};
          -webkit-box-decoration-break: ${HIGHLIGHT_STYLES.removed.WebkitBoxDecorationBreak};
        `;
      case 'unchanged':
      default:
        return '';
    }
  }}
`;

const TextHighlightContainer = styled.div`
  line-height: ${CONTAINER_STYLES.lineHeight};
  word-wrap: ${CONTAINER_STYLES.wordWrap};
  font-size: ${CONTAINER_STYLES.fontSize};
  color: ${CONTAINER_STYLES.color};

  /* Handle inline elements properly */
  display: inline;
`;

type HighlightedTextProps = {
  originalText: string;
  newText: string;
  className?: string;
  style?: React.CSSProperties;
};

/**
 * Component that highlights differences between original and new plain text
 * Similar to Microsoft Word's track changes feature
 * For HTML content highlighting, use HighlightedHtml component instead
 */
export const HighlightedText: React.FC<HighlightedTextProps> = ({
  originalText,
  newText,
  className,
  style,
}) => {
  // Get text differences
  const segments = React.useMemo(() => {
    return getTextDifferences(originalText, newText);
  }, [originalText, newText]);

  // If no differences, just show the new text
  if (segments.length === 0) {
    return (
      <TextHighlightContainer className={className} style={style}>
        {newText}
      </TextHighlightContainer>
    );
  }

  // Render highlighted segments
  return (
    <TextHighlightContainer className={className} style={style}>
      {segments.map((segment, index) => {
        return (
          <HighlightedSpan
            key={index}
            highlightType={segment.isChanged ? segment.type : 'unchanged'}
          >
            {segment.text}
          </HighlightedSpan>
        );
      })}
    </TextHighlightContainer>
  );
};
